import {ChangeDetectionStrategy, Component, Input, OnInit, signal} from '@angular/core';
import {AsyncPipe, NgIf} from '@angular/common';
import {EMPTY, Observable} from 'rxjs';
import {catchError, finalize} from 'rxjs/operators';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatMenuModule} from '@angular/material/menu';
import {TranslateModule} from '@ngx-translate/core';
import {IconButtonComponent, LinkButtonComponent} from '@q9elements/ui-kit/components';
import {
  PermissionsService,
  Q9TrackDirective,
  Q9TransformPipe,
  SnackBarService,
  TakeUntilComponentDestroy
} from '@q9elements/ui-kit/common';
import {DiagramJsonHandlerService} from '../../../../../diagram-data/diagram-json-handler.service';
import {PopupManagerService} from '../../../../../core/services/popup-manager.service';
import {DiagramJSON} from '../../../../../shared/models/diagram-json.interface';
import {ClipboardService} from '../../../../services/clipboard.service';
import {AgentforceService} from '../../../../../core/services/agentforce.service';
import {
  AGENT_FORCE_DATA_TYPES,
  AgentForceDataType
} from '../../../../shared/models/left-toolbar.constants';
import {DiagramTypeConfigService} from '../../../../../core/services/diagram-type-config.service';

@Component({
  selector: 'app-agentforce-generate-actions',
  templateUrl: './agentforce-generate-actions.component.html',
  styleUrls: ['./agentforce-generate-actions.component.scss'],
  imports: [
    MatMenuModule,
    MatTooltipModule,
    TranslateModule,
    Q9TransformPipe,
    IconButtonComponent,
    AsyncPipe,
    NgIf,
    LinkButtonComponent,
    Q9TrackDirective
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class AgentforceGenerateActionsComponent
  extends TakeUntilComponentDestroy
  implements OnInit
{
  @Input() isViewMode: boolean;
  @Input() diagramJson$: Observable<DiagramJSON>;

  public readonly AGENT_FORCE_DATA_TYPES = AGENT_FORCE_DATA_TYPES;
  public isLoading = signal(false);
  public elementsGPTEnabled = this.permissionsService.hasAccessToGPTFeatures;
  public agentforceEnabled: boolean;

  constructor(
    private permissionsService: PermissionsService,
    private diagramJsonSvc: DiagramJsonHandlerService,
    private popupSvc: PopupManagerService,
    private agentforceService: AgentforceService,
    private clipboardService: ClipboardService,
    private diagramTypeConfigSvc: DiagramTypeConfigService,
    private snackBarService: SnackBarService
  ) {
    super();
  }

  ngOnInit() {
    this.diagramJson$.pipe(this.takeUntilComponentDestroy()).subscribe(() => {
      this.checkAgentForceEnabled();
    });
  }

  public generateAgentforceData(type: AgentForceDataType) {
    const diagramId = this.diagramJsonSvc.get().diagramId;
    const state = this.diagramJsonSvc.get().state;

    this.isLoading.set(true);

    return this.agentforceService
      .generateAgentforceData(diagramId, state, type)
      .pipe(
        catchError(() => {
          this.snackBarService.error('ASIDE_R.AGENTFORCE_GENERATION_FAILED');

          return EMPTY;
        }),
        finalize(() => this.isLoading.set(false))
      )
      .subscribe(res => {
        this.popupSvc.openAgentforceDialog(res);
        this.copyLinkToClipboard(res.instructions || res.testUtterances, type);
      });
  }

  private copyLinkToClipboard(value: string, type: string) {
    const msg =
      this.diagramTypeConfigSvc.isAgent() && type === 'test-utterances'
        ? 'TEST-UTTERANCES_GENARATED'
        : `${type.toUpperCase()}_COPIED`;

    this.clipboardService.copyToClipboard(value, 'ASIDE_R.AGENTFORCE_' + msg);
  }

  private checkAgentForceEnabled() {
    this.agentforceEnabled = this.permissionsService.canUseAgentforce();
  }
}
